import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MapIcon from '@mui/icons-material/Map';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import SettingsIcon from '@mui/icons-material/Settings';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import '../simulation-modern.scss';
import './Result/result.style.scss';
import { Tabs, Tab } from '@mui/material';
import SelectSimuOtherUser from '../SelectSimuOtherUser';
import { SimulationContext } from '../SimulationContext';

import StepParcels from './Steps/StepParcels';
import StepCrops from './Steps/StepCrops';
import StepConstraints from './Steps/StepConstraints';
import StepStrategies from './Steps/StepStrategies';
import StepParcelConstraint from './Steps/StepParcelConstraint';
import Recapitulatif from './Steps/Recapitulatif';
import { ModernButton } from './Steps/ModernComponents';
import useSimulationConfig from './simulationConfig.hook';
import { ExploitationContext, LoadingContext } from 'utils/context';
import { displayErrorMessage } from 'utils/tools_functions';
import { filterParcelFromActivityDate } from 'utils/Datagrid/CustomParcelstDataGridUtils';

const Simulation = ({
  exploitationSelected,
  setExploitation,
  onSimulationComplete,
  onSimulationStatusChange,
  goToResults,
}) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSimulationContextUpdated, setIsSimulationContextUpdated] = useState(false);
  const [hasLaunchedSimulation, setHasLaunchedSimulation] = useState(false);
  const { cultures } = useContext(SimulationContext);
  const { setLoading } = useContext(LoadingContext);
  const { exploitationContext } = useContext(ExploitationContext);

  const totalSAU = filterParcelFromActivityDate(
    exploitationContext?.parcels,
    exploitationContext?.forecastCampaign,
  )
    ?.reduce((sum, currentValue) => sum + currentValue.surface, 0)
    .toFixed(2);

  const STEPS = [
    { label: t('common.parcels'), icon: MapIcon },
    { label: t('common.cultures'), icon: AgricultureIcon },
    { label: t('common.constraints'), icon: SettingsIcon },
    { label: t('simulation.navigation.strategies'), icon: TrackChangesIcon },
    { label: t('simulation.navigation.parcelConstraints'), icon: PlaylistAddCheckIcon },
    { label: t('simulation.navigation.summary'), icon: AssignmentTurnedInIcon },
  ];

  const simulation = useSimulationConfig(
    exploitationSelected,
    onSimulationComplete,
    setIsSimulationContextUpdated,
  );

  // Informer le parent du statut de simulation
  useEffect(() => {
    if (onSimulationStatusChange) {
      onSimulationStatusChange(simulation.isSimulating);
    }
  }, [simulation.isSimulating, onSimulationStatusChange]);

  const validateMinMax = (items, getConfig, label) => {
    for (const item of items) {
      const config = getConfig(item);
      if (!config) continue;

      const { min, max, unit } = config;
      const minValue = parseFloat(min) || 0;
      const maxValue = parseFloat(max) || 0;

      if (unit === 'percent') {
        if (maxValue > 100) {
          toast.error(t('simulation.validation.percentMaxExceeded', { label }));
          return false;
        }
        if (minValue > 100) {
          toast.error(t('simulation.validation.percentMinExceeded', { label }));
          return false;
        }
      }

      if (maxValue > 0 && minValue > maxValue) {
        toast.error(t('simulation.validation.minExceedsMax', { label }));
        return false;
      }
    }
    return true;
  };

  const validateCultureMinMax = () =>
    validateMinMax(
      simulation.selectedCultures,
      (cultureId) => simulation.cultureMinMax[cultureId],
      t('common.culture'),
    );

  const validateConstraintMinMax = () =>
    validateMinMax(
      simulation.selectedConstraints,
      (name) => {
        const values = simulation.constraintValues[name];
        return values?.hasInput ? values : null;
      },
      t('simulation.validation.constraintLabel'),
    );

  const handleNext = () => {
    if (currentStep === 0 && simulation.selectedParcels.length === 0) {
      toast.error(t('simulation.common.selectAtLeastOneParcel'));
      return;
    }
    if (currentStep === 1 && simulation.selectedCultures.length < 2) {
      toast.error(t('simulation.validation.selectAtLeastTwoCultures'));
      return;
    }
    if (currentStep === 1 && !validateCultureMinMax()) {
      return;
    }
    if (currentStep === 2 && !validateConstraintMinMax()) {
      return;
    }
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleLaunchSimulation = async () => {
    setHasLaunchedSimulation(true);
    setLoading(false);

    toast.info(t('simulation.common.simulationInProgress'), {
      autoClose: false,
      isLoading: true,
      toastId: 'simulation-loading',
    });

    await simulation.launchSimulation();
  };

  useEffect(() => {
    if (!hasLaunchedSimulation) return;

    if (!simulation.isSimulating) {
      toast.dismiss('simulation-loading');

      if (simulation.simulationError) {
        toast.error(t('simulation.messages.simulationError'));
      } else {
        toast.success(t('simulation.messages.simulationSuccess'), {
          autoClose: false,
          onClick: goToResults,
          style: { cursor: 'pointer' },
        });
      }

      setHasLaunchedSimulation(false);
    }
  }, [simulation.isSimulating, simulation.simulationError, hasLaunchedSimulation]);

  const handleTabChange = (event, newValue) => {
    if (newValue > 0 && simulation.selectedParcels.length === 0) {
      toast.error(t('simulation.common.selectAtLeastOneParcel'));
      return;
    }
    if (newValue > 1 && simulation.selectedCultures.length < 2) {
      toast.error(t('simulation.validation.selectAtLeastTwoCultures'));
      return;
    }
    if (newValue > 1 && !validateCultureMinMax()) {
      return;
    }
    setCurrentStep(newValue);
  };

  const isRecapStep = currentStep === STEPS.length - 1;

  const parseSimulationErrors = (errorData) => {
    const messages = [];
    if (errorData?.data?.error_type) {
      messages.push(displayErrorMessage(errorData.data.error_type, errorData.data));
      return messages;
    }
    if (!errorData?.errors?.length) {
      return [typeof errorData === 'string' ? errorData : t('simulation.messages.unknownError')];
    }

    for (const error of errorData.errors) {
      if (error.err_code === 2000) {
        for (const errorInfos of error?.err_info || []) {
          if (errorInfos.length === 1) {
            messages.push(
              displayErrorMessage(error.err_code + '_ONE', {
                ...errorInfos[0],
                message: displayErrorMessage(
                  errorInfos[0].error_code,
                  errorInfos[0].additionalInfo,
                ),
              }),
            );
          } else {
            messages.push(t('simulation.messages.conflictingConstraints'));
            for (const e of errorInfos) {
              messages.push(displayErrorMessage(e.error_code, e?.additionalInfo));
            }
          }
        }
      } else {
        messages.push(displayErrorMessage(error.err_code, error.message));
      }
    }

    return messages;
  };

  const NavigationButtons = () => (
    <div className="simulationNav">
      {currentStep > 0 && (
        <ModernButton variant="secondary" onClick={handlePrevious}>
          {t('common.previous')}
        </ModernButton>
      )}
      {isRecapStep && (
        <ModernButton
          variant="primary"
          onClick={handleLaunchSimulation}
          disabled={simulation.isSimulating}
        >
          {simulation.isSimulating
            ? t('simulation.common.simulationInProgress')
            : t('simulation.buttons.launchSimulation')}
        </ModernButton>
      )}
      {!isRecapStep && (
        <ModernButton
          variant="primary"
          onClick={handleNext}
          disabled={!simulation.canProceedToStep(currentStep + 1)}
        >
          {t('common.next')}
        </ModernButton>
      )}
    </div>
  );

  const ErrorBanner = () => {
    if (!simulation.simulationError) return null;

    const errorMessages = parseSimulationErrors(simulation.simulationError);

    return (
      <div className="simulation-error-banner">
        <div className="errorBannerText">
          <strong>{t('simulation.errorBanner.title')}</strong>
          <span>
            {t('simulation.common.jobId')}: {simulation?.jobId}
          </span>
          {errorMessages.map((msg, index) => (
            <span key={index} className="errorBannerMessage">
              {msg}
            </span>
          ))}
        </div>
        <button
          className="errorBannerClose"
          onClick={simulation.clearSimulationError}
          aria-label={t('common.close')}
        >
          <CloseIcon />
        </button>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <StepParcels
            searchTerm={simulation.parcelSearchTerm}
            setSearchTerm={simulation.setParcelSearchTerm}
            viewMapParcels={simulation.viewMapParcels}
            setViewMapParcels={simulation.setViewMapParcels}
            mapCoordinates={simulation.mapCoordinates}
            filteredParcels={simulation.filteredParcels}
            selectedParcels={simulation.selectedParcels}
            setSelectedParcels={simulation.setSelectedParcels}
            exploitationSelected={exploitationSelected}
            toggleSelection={simulation.toggleSelection}
          />
        );

      case 1:
        return (
          <StepCrops
            searchTerm={simulation.cultureSearchTerm}
            setSearchTerm={simulation.setCultureSearchTerm}
            filteredCultures={simulation.filteredCultures}
            selectedCultures={simulation.selectedCultures}
            setSelectedCultures={simulation.setSelectedCultures}
            cultureMinMax={simulation.cultureMinMax}
            setCultureMinMax={simulation.setCultureMinMax}
            toggleCultureSelection={simulation.toggleCultureSelection}
            updateCultureMinMax={simulation.updateCultureMinMax}
            totalSurface={totalSAU}
            fixedCultureIds={simulation.fixedCultureIds}
          />
        );

      case 2:
        return (
          <StepConstraints
            selectedConstraints={simulation.selectedConstraints}
            constraintValues={simulation.constraintValues}
            toggleConstraintSelection={simulation.toggleConstraintSelection}
            updateConstraintValue={simulation.updateConstraintValue}
            coProducts={simulation.coProducts}
            totalSurface={totalSAU}
          />
        );

      case 3:
        return (
          <StepStrategies
            selectedStrategies={simulation.selectedStrategies}
            setSelectedStrategies={simulation.setSelectedStrategies}
            yearsToSimulate={simulation.yearsToSimulate}
            setYearsToSimulate={simulation.setYearsToSimulate}
            toggleSelection={simulation.toggleSelection}
          />
        );

      case 4:
        return (
          <StepParcelConstraint
            mapCoordinates={simulation.mapCoordinates}
            parcelConstraints={simulation.parcelConstraints}
            showConstraintModal={simulation.showConstraintModal}
            currentConstraint={simulation.currentConstraint}
            selectedParcels={simulation.selectedParcels}
            exploitationSelected={exploitationSelected}
            toggleModal={simulation.toggleModal}
            deleteConstraint={simulation.deleteConstraint}
            updateConstraintField={simulation.updateConstraintField}
            toggleParcelConstraintField={simulation.toggleParcelConstraintField}
            saveConstraint={simulation.saveConstraint}
            editConstraint={simulation.editConstraint}
            cultures={simulation.culturesResult}
            selectedCultureIds={simulation.selectedCultures}
            forecastCampaign={exploitationSelected?.forecastCampaign}
            yearsToSimulate={simulation.yearsToSimulate}
          />
        );

      case 5:
        const {
          parcelsList,
          culturesList,
          constraintsList,
          strategiesList,
          parcelConstraintsList,
        } = simulation.getRecapData();

        return (
          <Recapitulatif
            parcelsList={parcelsList}
            culturesList={culturesList}
            constraintsList={constraintsList}
            parcelConstraintsList={parcelConstraintsList}
            strategiesList={strategiesList}
            yearsToSimulate={simulation.yearsToSimulate}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="result simulation-app">
      <nav className="sidebar">
        <Tabs
          orientation="vertical"
          value={currentStep}
          onChange={handleTabChange}
          className="tabs"
        >
          {STEPS.map((step, index) => (
            <Tab key={index} icon={<step.icon />} label={step.label} className="tab" />
          ))}
        </Tabs>
      </nav>

      <div className="container">
        <ErrorBanner />

        <div className="simulationHeader">
          <SelectSimuOtherUser
            isSimulationContextUpdated={isSimulationContextUpdated}
            updateSimulationParams={simulation.updateSimulationParams}
            cultures={cultures}
            exploitationId={exploitationSelected?.id}
            setHasExistingSimulation={simulation.setHasExistingSimulation}
          />
          <NavigationButtons />
        </div>
        <div className="simulationContent">
          {renderStepContent()}

          <NavigationButtons />
        </div>
      </div>
    </div>
  );
};

export default Simulation;
